/* eslint-disable react/function-component-definition */
import { ComponentType, ElementType, FC } from 'react';
import Role, {
  householdAdminRoles,
  householdDataViewerRoles,
  internalAdminRoles,
  wardDataAdminRoles
} from '../types/enums';
import useAuth from './auth';
import {
  useCampaignsEnabled,
  useSendToCalculatorEnabled,
} from '../components/organisation-settings/organisationSettingsProvider';

export const hasCreateCampaignAccess = (role: Role, campaignsEnabled: boolean): boolean => (
  (campaignsEnabled && role === Role.OrganisationAdmin)
  || internalAdminRoles.includes(role)
);

export const hasCampaignAccess = (role: Role, campaignsEnabled: boolean): boolean => (
  campaignsEnabled || internalAdminRoles.includes(role)
);

export const hasSendToCalculatorAccess = (role: Role, sendToCalculatorEnabled: boolean): boolean => (
  sendToCalculatorEnabled && householdAdminRoles.includes(role)
);

// Role component wrapper needs a versatile type so that it can be used for all components while still being type safe
const withRoleAccess = (allowedRoles: string[]) => <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> | ElementType => {
  const WithRoleAccess: FC<P> = (props) => {
    const authUser = useAuth();
    return allowedRoles.includes(authUser.role) ? <WrappedComponent {...props} /> : null;
  };
  return WithRoleAccess;
};

const withCampaignAccess = <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> | ElementType => {
  const WithCampaignAccess: FC<P> = (props) => {
    const { campaignsEnabled } = useCampaignsEnabled();
    const authUser = useAuth();
    return hasCampaignAccess(authUser.role, campaignsEnabled) ? <WrappedComponent {...props} /> : null;
  };
  return WithCampaignAccess;
};
const withCreateCampaignAccess = <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> | ElementType => {
  const WithCreateCampaignAccess: FC<P> = (props) => {
    const { campaignsEnabled } = useCampaignsEnabled();
    const authUser = useAuth();
    return hasCreateCampaignAccess(authUser.role, campaignsEnabled) ? <WrappedComponent {...props} /> : null;
  };
  return WithCreateCampaignAccess;
};

const withSendToCalculatorAccess = <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> | ElementType => {
  const WithSendToCalculatorAccess: FC<P> = (props) => {
    const { sendToCalculatorEnabled } = useSendToCalculatorEnabled();
    const authUser = useAuth();
    // Send to the Calculator is enabled, and the user has an eligible role
    return hasSendToCalculatorAccess(authUser.role, sendToCalculatorEnabled)
      ? <WrappedComponent {...props} /> : null;
  };
  return WithSendToCalculatorAccess;
};
export const superAdminAccess = withRoleAccess([Role.SuperAdmin]);
export const clientServicesAccess = withRoleAccess(internalAdminRoles);
export const householdAdminAccess = withRoleAccess(householdAdminRoles);
export const campaignAccess = withCampaignAccess;
export const createCampaignAccess = withCreateCampaignAccess;
export const sendToCalculatorAccess = withSendToCalculatorAccess;
export const householdDataAccess = withRoleAccess(householdDataViewerRoles);
export const wardAdminAccess = withRoleAccess(wardDataAdminRoles);
